import { useEffect, useMemo, useState } from 'react';
import { Inbox, Check, X, Pencil, Clock, UserCheck, Workflow, CheckCircle2, XCircle, Coins } from 'lucide-react';
import { useAgentStudioNavigate } from '../lib/routes';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { EmptyState, Skeleton } from '../components/ui/States';
import { Modal } from '../components/ui/Modal';
import { Textarea } from '../components/ui/Field';
import { approvalsService } from '../services/approvalsService';
import { budgetService, type GrantRequest } from '../services/budgetService';
import { formatRelativeTime, formatUSD } from '../lib/utils';
import { toast } from '../store/uiStore';
import { ApiError } from '../services/apiClient';
import type { ApprovalTask, Person } from '../types';
import { AGENT_STUDIO_PERMISSIONS as P, useAgentStudioPermissions } from '../lib/permissions';

export function ActivitiesPage() {
  const navigate = useAgentStudioNavigate();
  const canDecide = useAgentStudioPermissions().has(P.activitiesDecide);
  const [tasks, setTasks] = useState<ApprovalTask[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ApprovalTask | null>(null);
  const [editText, setEditText] = useState('');
  const [grants, setGrants] = useState<GrantRequest[]>([]);

  async function load() {
    const [t, p, g] = await Promise.all([
      approvalsService.listForUser(),
      budgetService.listPeople(),
      budgetService.listPendingGrants().catch(() => []),
    ]);
    setTasks(t);
    setPeople(p);
    setGrants(g);
    setLoading(false);
  }

  async function decideGrant(id: string, decision: 'approve' | 'reject') {
    try {
      await budgetService.decideGrant(id, decision);
      toast.success(decision === 'approve' ? 'Saldo aprovado.' : 'Pedido recusado.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao decidir.');
    }
    load();
  }
  useEffect(() => {
    load();
  }, []);

  const personName = (id: string) => people.find((p) => p.id === id)?.name ?? id;

  const { mine, others, history } = useMemo(() => {
    const mine: ApprovalTask[] = [];
    const others: ApprovalTask[] = [];
    const history: ApprovalTask[] = [];
    tasks.forEach((t) => {
      if (t.status !== 'pending') history.push(t);
      else mine.push(t);
    });
    return { mine, others, history };
  }, [tasks]);

  async function decide(task: ApprovalTask, decision: 'approve' | 'reject', editedOutput?: string) {
    await approvalsService.decide(task.id, decision, editedOutput);
    toast.success(decision === 'approve' ? 'Aprovado.' : 'Rejeitado.');
    setEditing(null);
    load();
  }

  return (
    <>
      <PageHeader
        title="Minhas Atividades"
        subtitle={`${mine.length} aprovação(ões) aguardando você`}
      />
      <div className="flex-1 overflow-y-auto scrollbar-thin p-8">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-8">
            {grants.length > 0 && (
              <section>
                <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink">
                  <Coins className="h-4 w-4 text-brand-orange" /> Pedidos de saldo (área)
                  <Badge tone="orange">{grants.length}</Badge>
                </h2>
                <div className="space-y-3">
                  {grants.map((g) => (
                    <Card key={g.id} className="flex flex-wrap items-center gap-3 p-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-ink">
                          {g.requesterName ?? g.personId} pediu <strong>{formatUSD(g.amountUsd)}</strong>
                          <span className="text-slate"> · área {g.areaName ?? g.areaId}</span>
                        </p>
                        <p className="text-xs text-slate">{g.reason || 'Sem justificativa.'} · {formatRelativeTime(g.createdAt)}</p>
                      </div>
                      {canDecide && (
                        <div className="flex gap-1.5">
                          <Button size="sm" variant="primary" icon={Check} onClick={() => decideGrant(g.id, 'approve')}>Aprovar</Button>
                          <Button size="sm" variant="danger" icon={X} onClick={() => decideGrant(g.id, 'reject')}>Recusar</Button>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </section>
            )}

            <section>
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink">
                <UserCheck className="h-4 w-4 text-brand-orange" /> Aguardando você
                {mine.length > 0 && <Badge tone="orange">{mine.length}</Badge>}
              </h2>
              {mine.length === 0 ? (
                <EmptyState icon={Inbox} title="Tudo em dia" description="Nenhuma aprovação pendente para você no momento." />
              ) : (
                <div className="space-y-3">
                  {mine.map((t) => (
                    <TaskCard
                      key={t.id}
                      task={t}
                      personName={personName}
                      onOpenFlow={() => navigate(`/flows/${t.flowId}`)}
                      actions={canDecide ? (
                        <>
                          <Button size="sm" variant="primary" icon={Check} onClick={() => decide(t, 'approve')}>Aprovar</Button>
                          <Button size="sm" variant="secondary" icon={Pencil} onClick={() => { setEditing(t); setEditText(t.outputPreview ?? ''); }}>Editar</Button>
                          <Button size="sm" variant="danger" icon={X} onClick={() => decide(t, 'reject')}>Rejeitar</Button>
                        </>
                      ) : undefined}
                    />
                  ))}
                </div>
              )}
            </section>

            {others.length > 0 && (
              <section>
                <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink">
                  <Clock className="h-4 w-4 text-slate" /> Aguardando outros aprovadores
                </h2>
                <div className="space-y-3">
                  {others.map((t) => (
                    <TaskCard key={t.id} task={t} personName={personName} onOpenFlow={() => navigate(`/flows/${t.flowId}`)} muted />
                  ))}
                </div>
              </section>
            )}

            {history.length > 0 && (
              <section>
                <h2 className="mb-3 text-sm font-semibold text-ink">Histórico</h2>
                <div className="space-y-3">
                  {history.map((t) => (
                    <TaskCard key={t.id} task={t} personName={personName} onOpenFlow={() => navigate(`/flows/${t.flowId}`)} muted />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {/* Editar saída e aprovar */}
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Editar saída e aprovar"
        width="max-w-xl"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button variant="primary" icon={Check} onClick={() => editing && decide(editing, 'approve', editText)}>Aprovar com edição</Button>
          </>
        }
      >
        {editing && (
          <>
            <p className="mb-2 text-sm text-slate">{editing.message}</p>
            <Textarea rows={6} value={editText} onChange={(e) => setEditText(e.target.value)} className="font-mono text-xs" />
          </>
        )}
      </Modal>
    </>
  );
}

function TaskCard({
  task,
  personName,
  actions,
  onOpenFlow,
  muted,
}: {
  task: ApprovalTask;
  personName: (id: string) => string;
  actions?: React.ReactNode;
  onOpenFlow: () => void;
  muted?: boolean;
}) {
  const overdue = false; // (mock) — cálculo de SLA real virá com o backend
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-ink">{task.nodeLabel}</h3>
            {task.status === 'pending' ? (
              <Badge tone="warning" dot>Pendente</Badge>
            ) : task.status === 'approved' ? (
              <Badge tone="success"><CheckCircle2 className="h-3 w-3" /> Aprovado</Badge>
            ) : (
              <Badge tone="danger"><XCircle className="h-3 w-3" /> Rejeitado</Badge>
            )}
            <Badge tone="neutral"><Clock className="h-3 w-3" /> SLA {task.slaMinutes}min</Badge>
          </div>
          <button onClick={onOpenFlow} className="mt-1 inline-flex items-center gap-1 text-xs text-slate hover:text-brand-orange">
            <Workflow className="h-3.5 w-3.5" /> {task.flowName}
          </button>
        </div>
        <span className="shrink-0 text-xs text-slate">{formatRelativeTime(task.createdAt)}</span>
      </div>

      <p className="mt-2 text-sm text-ink">{task.message}</p>

      {task.outputPreview && (
        <pre className="mt-2 max-h-32 overflow-auto scrollbar-thin whitespace-pre-wrap rounded-lg bg-cloud p-2.5 font-mono text-[11px] text-slate">
          {task.outputPreview}
        </pre>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs text-slate">
          <UserCheck className="h-3.5 w-3.5" />
          Aprovadores: {task.approverIds.map(personName).join(', ')}
          {task.decidedBy && <span className="text-ink"> · decidido por {personName(task.decidedBy)}</span>}
        </div>
        {actions && <div className="flex flex-wrap gap-1.5">{actions}</div>}
      </div>
      {overdue && <p className="mt-1 text-xs text-danger">Fora do SLA</p>}
    </Card>
  );
}
